import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import { getCompanies, login } from '../../modules/auth';
import LoginForm from './LoginForm';

const validationSchema = yup.object({
  username: yup
    .string('Enter your email')
    .email('Enter a valid email')
    .required('Email is required'),
  password: yup
    .string('Enter your password')
    .min(4, 'Password should be of minimum 4 characters length')
    .required('Password is required'),
  company: yup.string('Select your company').required('Company is required'),
});

const LoginFormContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSeratchParams] = useSearchParams();
  const linkUrl = searchParams.get('linkurl') !== null && searchParams.get('linkurl') !== '' ? searchParams.get('linkurl') : '/';
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const from = location.state?.from || linkUrl || '/';

  const { companies, authLogin, authLoginError, user } = useSelector(
    ({ auth }) => ({
      companies: auth.companies,
      authLogin: auth.authLogin,
      authLoginError: auth.authLoginError,
      user: auth.user,
    }),
  );

  
  // 1. 현재 접속한 기기가 모바일 환경인지 판별합니다.
  const isMobileOS = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // 컴포넌트 처음 렌더링 시 회사 목록 호출
  useEffect(() => {
    dispatch(getCompanies());
  }, []);

  // 로그인 성공/실패 핸들링
  useEffect(() => {
    if (authLoginError) {
      let message =
        typeof authLoginError.response !== 'undefined'
          ? authLoginError.response.data.message
          : authLoginError.message;
      enqueueSnackbar(message, { variant: 'error' });
    }

    if (authLogin && user) {
      navigate(from, { replace: true });
      try {
        localStorage.setItem('user', JSON.stringify(user));
      } catch (e) {
        console.error('localStorage is not working');
      }
    }
  }, [authLogin, authLoginError, user, navigate, enqueueSnackbar, from]);

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
      company: '',
      loginType: localStorage.getItem('loginMode') === 'car_service' ? 'car' : '',
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting }) => {
      const usernameInput = document.querySelector('input[name="username"]')?.value || values.username;
      const passwordInput = document.querySelector('input[name="password"]')?.value || values.password;
      
      const clickedElementValue = document.activeElement?.value;
      let activeLoginType = values.loginType;

      if (clickedElementValue === 'car') {
        activeLoginType = 'car';
      } else if (clickedElementValue === 'normal') {
        activeLoginType = '';
      }

      if (!usernameInput || !passwordInput || !values.company) {
        enqueueSnackbar('아이디, 비밀번호, 회사를 모두 입력해주세요.', { variant: 'error' });
        setSubmitting(false);
        return;
      }

      try {
        sessionStorage.setItem('email', usernameInput);
      } catch (e) {
        console.error('sessionStorage 이메일 기록 오류:', e);
      }

      try {
        if (activeLoginType === 'car') {
          localStorage.setItem('loginMode', 'car_service');
        } else {
          localStorage.removeItem('loginMode');
        }
      } catch (e) {
        console.error('localStorage 연동 오류:', e);
      }

      localStorage.setItem('current_company', values.company);

      let dataToSubmit = {
        username: usernameInput,
        password: passwordInput,
        company: values.company, 
        loginType: activeLoginType,
      };

      setSubmitting(true);
      dispatch(login(dataToSubmit));
      setSubmitting(false);
    },
  });


  // 회사 정보 자동 매핑 (HCINFO)
  useEffect(() => {
    if (companies && companies.length > 0) {
      const hasHCINFO = companies.find(
        c => c.companyNm === 'HCINFO' || c.companyCd === 'HCINFO'
      );
      
      if (hasHCINFO) {
        formik.setFieldValue('company', hasHCINFO.companyCd);
      } else {
        formik.setFieldValue('company', companies[0].companyCd);
      }
    }
  }, [companies]);

  // [수정] 모바일 환경에서만 자동 로그인 트리거 작동 (PC 차단 옵션 반영)
  useEffect(() => {
    
    // [핵심 조건] PC 브라우저 환경이라면 자동 제출 프로세스를 아예 시작하지 않고 조기 종료합니다.
    if (!isMobileOS) {
      return;
    }

    // 2. 방금 로그아웃해서 sessionStorage에 증표가 남아있다면 자동 로그인을 하지 않고 대기합니다.
    const justLoggedOut = sessionStorage.getItem('justLoggedOut') === 'true';
    if (justLoggedOut) {
      return; 
    }

    // 3. 모바일 기기(아이폰/갤럭시)에서 새롭게 접근한 유저일 때만 자동 로그인을 진행합니다.
    const checkAutofillInterval = setInterval(() => {
      const usernameInput = document.querySelector('input[name="username"]')?.value;
      const passwordInput = document.querySelector('input[name="password"]')?.value;
      const currentCompany = formik.values.company;

      if (usernameInput && passwordInput && currentCompany) {
        clearInterval(checkAutofillInterval);
        formik.submitForm(); 
      }
    }, 500);

    return () => clearInterval(checkAutofillInterval);
  }, [formik.values.company]);

  return <LoginForm companies={companies} formik={formik} isMobileOS={isMobileOS}/>;
};

export default LoginFormContainer;
